import {useCallback, useEffect} from "react";
import {useComputed, useSignal} from "@preact/signals-react";
import {isEmpty, postSubmit} from "@ui-seeker/utils";

import {BACKEND_APIS} from "@constants";
import {useToast} from "@hooks";
import {isLoadingCommonFilters, commonFiltersValue, customerID} from "@signals";
import {addLoadingInUsersActivitiesLoadingTops, removeLoadingInUsersActivitiesLoadingTops, appliedCustomerIDs} from "../@signals";
import {calculateDiff} from "../@utils";
import {TABS_TOP} from "../@constants";


export default function useUserActivitiesTops(api) {
	const { showToast } = useToast()
	const isLoadingData = useSignal(false)
	const isLoadingDataPrev = useSignal(false)
	const data = useSignal({})
	const dataPrev = useSignal({})

	const isLoading = useComputed(() => isLoadingData.value || isLoadingDataPrev.value)
	const dailyData = useComputed(() => {
		if (isEmpty(data.value))
			return []
		return data.value.dailyData.map(row => {
			const prevRow = (dataPrev.value.dailyData?.[row.id]?.[row.customerID]) || {};
			const current = row.dailyVisits
			const previous = prevRow.dailyVisits
			const activeUsers = row.activeUsers
			const prevActiveUsers = prevRow.activeUsers
			const totalUsers = row.totalUsers || 0
			return {
				...row, current, previous,
				diff: calculateDiff(current, previous),
				activeUsers, prevActiveUsers, totalUsers,
				totalDiff: totalUsers ? (activeUsers * 100) / totalUsers : 0,
				activeDiff: calculateDiff(activeUsers, prevActiveUsers),
			}
		})
	})
	const submitData = useComputed(() => {
		if (isEmpty(data.value))
			return []
		return data.value.submitData.map(row => {
			const prevRow = (dataPrev.value.dailyData?.[row.id]?.[row.customerID]) || {};
			const current = row.numSubmits
			const previous = prevRow.numSubmits
			return {
				...row, current, previous,
				diff: calculateDiff(current, previous)
			}
		})
	})

	useEffect(() => {
		if (isLoadingCommonFilters.value)
			handleSubmit()
	}, [isLoadingCommonFilters.value]);

	useEffect(() => {
		if (isLoading.value) addLoadingInUsersActivitiesLoadingTops(api)
		else removeLoadingInUsersActivitiesLoadingTops(api)
	}, [isLoading.value])

	const handleSubmit = () => {
	    const filters = { ...commonFiltersValue.value };
        if (customerID.value === 0) {
            appliedCustomerIDs.value = filters.customerSelectedID || [];
        } else {
            appliedCustomerIDs.value = [customerID.value];
            if (api === TABS_TOP.CUSTOMERS) {
                return;
            }
        }

        if (!isLoadingData.value)
			isLoadingData.value = true
		postSubmit(`${BACKEND_APIS.ADMIN}/user-activities/tops/${api}`, commonFiltersValue.value).then(res => {
			data.value = res
			isLoadingData.value = false
		}).catch(() => {
			showToast.error()
			data.value = {}
			isLoadingData.value = false
		})

		if (!isLoadingDataPrev.value)
			isLoadingDataPrev.value = true
		postSubmit(`${BACKEND_APIS.ADMIN}/user-activities/tops/${api}`, {...commonFiltersValue.value, isPrev: true}).then(res => {
			dataPrev.value = res
			isLoadingDataPrev.value = false
		}).catch(() => {
			showToast.error()
			dataPrev.value = {}
			isLoadingDataPrev.value = false
		})
	}

	const handleExcel = useCallback(() => showToast.promiseExcel(`${BACKEND_APIS.ADMIN}/user-activities/tops/${api}/excel`, {...commonFiltersValue.value}, null, {
        onError: data => {
            if (data.toString().includes('Error')) return 'Excel file size exceeded'
        }
    }))

	return {
		isLoading: isLoading.value,
		dailyData: dailyData.value,
		submitData: submitData.value,

		handleExcel
	}

}